(function () {
  'use strict';

  if (customElements.get('teak-cushion-configurator')) return;

  var SWATCH_COLOURS = {
    black: '#1E1E1E', coal: '#3A3A38', charcoal: '#3A3A38', navy: '#26364F', denim: '#3B5473',
    stone: '#E4E1DA', white: '#F4F2ED', flax: '#D6C7A8', oatmeal: '#DCD3C2', sage: '#A8A997',
    beige: '#D9C9AE', taupe: '#B3A492', tan: '#C6A379', cream: '#EFE7DA', grey: '#9A9A96',
    gray: '#9A9A96', green: '#5C6B52', blue: '#3C5A78', yellow: '#D2B44C', teak: '#C99A5B'
  };

  function clean(value) { return String(value == null ? '' : value).trim(); }

  function extractSize(value) {
    var match = clean(value).toLowerCase().match(/\b(\d{2,3})\s*(?:cm)?\b/);
    return match ? match[1] : '';
  }

  function swatchColour(name) {
    var text = clean(name).toLowerCase();
    var primary = text.split(/\s*(?:&|\/|\band\b)\s*/)[0];
    var candidates = [primary, text];
    var keys = Object.keys(SWATCH_COLOURS);
    for (var c = 0; c < candidates.length; c += 1) {
      for (var i = 0; i < keys.length; i += 1) {
        if (candidates[c].indexOf(keys[i]) !== -1) return SWATCH_COLOURS[keys[i]];
      }
    }
    return '';
  }

  function swatchBackground(name) {
    var text = clean(name).toLowerCase();
    var parts = text.split(/\s*(?:&|\/|\band\b)\s*/).filter(Boolean);
    var first = swatchColour(parts[0] || text) || '#D8D3C9';
    if (text.indexOf('stripe') !== -1 || parts.length > 1) {
      var second = swatchColour(parts[1] || '') || '#F4F2ED';
      return 'repeating-linear-gradient(135deg, ' + first + ' 0 10px, ' + second + ' 10px 20px)';
    }
    return first;
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  function cloneSelection(selection) {
    return {
      productHandle: selection.productHandle || '',
      variantId: String(selection.variantId || ''),
      choiceKey: selection.choiceKey || '',
      label: selection.label || '',
      qty: Math.max(1, parseInt(selection.qty, 10) || 1)
    };
  }

  class TeakCushionConfigurator extends HTMLElement {
    connectedCallback() {
      // Shopify's section morphing can copy data attributes onto a fresh element.
      if (this._teakCushionReady) return;
      this._teakCushionReady = true;
      this.dataset.ready = 'true';

      this.productRoot = this.closest('[data-teak-pshow]');
      this.dataElement = this.querySelector('[data-cushion-configurator-data]');
      this.data = this.readData();
      this.products = Array.isArray(this.data.products) ? this.data.products : [];
      this.mainProduct = this.data.mainProduct || {};
      this.mainVariants = Array.isArray(this.mainProduct.variants) ? this.mainProduct.variants : [];
      this.currency = this.data.currency || 'AUD';
      this.styleName = this.dataset.selectorStyle || 'visual-modal';
      this.mainVariantInput = this.productRoot ? this.productRoot.querySelector('[data-teak-pdetails-variant-id]') : null;
      this.mainQtyInput = this.productRoot ? this.productRoot.querySelector('[data-teak-pdetails-qty]') : null;

      this.offer = this.querySelector('[data-cushion-open]');
      this.offerIcon = this.querySelector('[data-cushion-offer-icon]');
      this.offerTitle = this.querySelector('[data-cushion-offer-title]');
      this.offerMeta = this.querySelector('[data-cushion-offer-meta]');
      this.offerPrice = this.querySelector('[data-cushion-offer-price]');
      this.offerAction = this.querySelector('[data-cushion-offer-action]');
      this.offerRemove = this.querySelector('[data-cushion-offer-remove]');

      this.shell = this.querySelector('[data-cushion-shell]');
      this.studio = this.querySelector('[data-cushion-studio]');
      this.productField = this.querySelector('[data-cushion-product-field]');
      this.productSelect = this.querySelector('[data-cushion-product-select]');
      this.tilesHost = this.querySelector('[data-cushion-tiles]');
      this.fitLabel = this.querySelector('[data-cushion-fit]');
      this.notice = this.querySelector('[data-cushion-notice]');
      this.sceneImage = this.querySelector('[data-cushion-scene]');
      this.caption = this.querySelector('[data-cushion-caption]');
      this.sceneAction = this.querySelector('[data-cushion-scene-action]');
      this.summary = this.querySelector('[data-cushion-summary]');
      this.confirmButton = this.querySelector('[data-cushion-confirm]');
      this.status = this.querySelector('[data-cushion-status]');

      // committed = what goes to the cart; draft = what is being edited in the studio.
      this.committed = [];
      this.committedTouched = false;
      this.draft = [];
      this.draftTouched = false;
      this.focusVariantId = '';
      this.selectedProductHandle = '';
      this.lastMainVariantId = '';
      this.lastMainQty = this.mainQuantity();
      this.noticeMessage = '';
      this.isOpen = false;
      this.returnFocus = null;

      if (!this.products.length || !this.productRoot || !this.shell) {
        this.hidden = true;
        return;
      }

      if (this.styleName === 'inline-studio') {
        var portal = this.productRoot.querySelector('[data-cushion-inline-portal]');
        if (portal) portal.appendChild(this.shell);
        this.studio.setAttribute('role', 'region');
        if (this.offer) {
          this.offer.removeAttribute('aria-haspopup');
          this.offer.setAttribute('aria-expanded', 'false');
        }
      } else {
        document.body.appendChild(this.shell);
        this.studio.setAttribute('role', 'dialog');
        this.studio.setAttribute('aria-modal', 'true');
      }

      this.onDocumentKeydown = this.handleDocumentKeydown.bind(this);
      document.addEventListener('keydown', this.onDocumentKeydown);
      this.bindEvents();
      this.refresh();
    }

    disconnectedCallback() {
      if (this.onDocumentKeydown) document.removeEventListener('keydown', this.onDocumentKeydown);
      if (this.shell && !this.contains(this.shell)) this.shell.remove();
      this.setPageLocked(false);
    }

    readData() {
      try {
        return JSON.parse(this.dataElement ? this.dataElement.textContent : '{}');
      } catch (error) {
        console.warn('Unable to read matching cushion data', error);
        return {};
      }
    }

    // ---------- money ----------
    formatMoney(cents) {
      var value = (parseInt(cents, 10) || 0) / 100;
      var text;
      try {
        text = new Intl.NumberFormat(this.data.locale || 'en-AU', { style: 'currency', currency: this.currency, currencyDisplay: 'narrowSymbol' }).format(value);
      } catch (error) {
        text = '$' + value.toFixed(2);
      }
      return text.replace(/\.00\b/, '');
    }

    isMobile() {
      return !!(window.matchMedia && window.matchMedia('(max-width: 749px)').matches);
    }

    // ---------- events ----------
    bindEvents() {
      var self = this;
      // Mobile sheet: drag the handle or the header downwards to close.
      var grabZone = this.shell ? this.shell.querySelector('[data-cushion-grab]') : null;
      var header = this.shell ? this.shell.querySelector('.teak-cushion-configurator__header') : null;
      [grabZone, header].forEach(function (zone) {
        if (!zone) return;
        var startY = null;
        zone.addEventListener('touchstart', function (event) { startY = event.touches[0].clientY; }, { passive: true });
        zone.addEventListener('touchmove', function (event) {
          if (startY === null) return;
          if (event.touches[0].clientY - startY > 80) { startY = null; self.close(); }
        }, { passive: true });
        zone.addEventListener('touchend', function () { startY = null; });
        if (zone === grabZone) zone.addEventListener('click', function () { self.close(); });
      });
      if (this.offer) this.offer.addEventListener('click', this.open.bind(this));
      if (this.offerRemove) {
        this.offerRemove.addEventListener('click', function (event) {
          event.preventDefault(); event.stopPropagation();
          self.removeAll();
        });
      }

      // The shell is portalled outside the product form for modal and drawer layouts.
      // Handle its actions during capture so theme-level handlers cannot swallow them.
      if (this.shell) {
        this.shell.addEventListener('click', function (event) {
          var qtyButton = event.target.closest('[data-cushion-qty]');
          var removeButton = event.target.closest('[data-cushion-remove-variant]');
          var addButton = event.target.closest('[data-cushion-add]');
          var tile = event.target.closest('[data-cushion-tile]');
          var action = event.target.closest('[data-cushion-confirm], [data-cushion-close]');

          if (qtyButton && self.shell.contains(qtyButton)) {
            event.preventDefault(); event.stopPropagation();
            self.changeQty(qtyButton.dataset.variantId, parseInt(qtyButton.dataset.cushionQty, 10));
            return;
          }
          if (removeButton && self.shell.contains(removeButton)) {
            event.preventDefault(); event.stopPropagation();
            self.removeFromDraft(removeButton.dataset.variantId);
            return;
          }
          if (addButton && self.shell.contains(addButton)) {
            event.preventDefault(); event.stopPropagation();
            self.addVariant(addButton.dataset.productHandle, addButton.dataset.variantId);
            return;
          }
          if (tile && self.shell.contains(tile)) {
            event.preventDefault(); event.stopPropagation();
            self.viewTile(tile.dataset.productHandle, tile.dataset.variantId);
            return;
          }
          if (!action || !self.shell.contains(action)) return;
          event.preventDefault();
          if (action.matches('[data-cushion-confirm]')) self.confirm();
          else self.close();
        }, true);

        this.shell.addEventListener('keydown', function (event) {
          var tile = event.target.closest('[data-cushion-tile]');
          if (!tile) return;
          if (event.target !== tile) return; // buttons inside the tile handle their own keys
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            self.viewTile(tile.dataset.productHandle, tile.dataset.variantId);
            return;
          }
          var keys = ['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown', 'Home', 'End'];
          if (keys.indexOf(event.key) === -1) return;
          var tiles = Array.from(self.tilesHost.querySelectorAll('[data-cushion-tile]'));
          var index = tiles.indexOf(tile);
          if (index === -1) return;
          event.preventDefault();
          var next;
          if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = tiles.length - 1;
          else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + tiles.length) % tiles.length;
          else next = (index + 1) % tiles.length;
          tiles[next].focus();
        });
      }

      if (this.productSelect) {
        this.productSelect.addEventListener('change', function (event) {
          self.selectedProductHandle = event.target.value;
          self.noticeMessage = '';
          self.render();
        });
      }

      if (this.mainQtyInput) {
        var onQty = function () { self.handleMainQtyChange(); };
        this.mainQtyInput.addEventListener('change', onQty);
        this.mainQtyInput.addEventListener('input', onQty);
      }
    }

    // ---------- main product ----------
    mainQuantity() {
      var value = this.mainQtyInput ? parseInt(this.mainQtyInput.value, 10) : 1;
      return Math.max(1, value || 1);
    }

    handleMainQtyChange() {
      var qty = this.mainQuantity();
      var previous = this.lastMainQty;
      this.lastMainQty = qty;
      if (qty === previous) return;
      // One colour that still mirrors the furniture quantity keeps following it.
      if (this.committed.length === 1 && !this.committedTouched && this.committed[0].qty === previous) {
        this.committed[0].qty = qty;
      }
      if (this.isOpen && this.draft.length === 1 && !this.draftTouched && this.draft[0].qty === previous) {
        this.draft[0].qty = qty;
      }
      this.render();
    }

    currentMainVariant() {
      var id = this.mainVariantInput ? this.mainVariantInput.value : this.mainProduct.initialVariantId;
      return this.mainVariants.find(function (variant) { return String(variant.id) === String(id); }) || this.mainVariants[0] || null;
    }

    currentMainVariantId() {
      var variant = this.currentMainVariant();
      return variant ? String(variant.id) : '';
    }

    currentMainSize() {
      var variant = this.currentMainVariant();
      var candidates = variant && Array.isArray(variant.options) ? variant.options.slice() : [];
      if (variant) candidates.push(variant.title);
      candidates.push(this.mainProduct.title || '');
      for (var i = 0; i < candidates.length; i += 1) {
        var size = extractSize(candidates[i]);
        if (size) return size;
      }
      return '';
    }

    mainLineLabel() {
      var variant = this.currentMainVariant();
      var title = clean(this.mainProduct.title);
      if (variant && clean(variant.title) && clean(variant.title).toLowerCase() !== 'default title') title += ' ' + clean(variant.title);
      return title;
    }

    // ---------- cushion products and variants ----------
    productRequiresSizeMatch(product) {
      if (product.requiresSizeMatch) return true;
      if (!this.currentMainSize()) return false;
      return (product.variants || []).some(function (variant) {
        return (variant.options || []).some(function (option) { return !!extractSize(option); });
      });
    }

    variantMatchesSize(product, variant) {
      if (!this.productRequiresSizeMatch(product)) return true;
      var size = this.currentMainSize();
      if (!size) return false;
      return (variant.options || []).some(function (option) { return extractSize(option) === size; });
    }

    matchingVariants(product) {
      return (product.variants || []).filter(function (variant) { return this.variantMatchesSize(product, variant); }, this);
    }

    availableVariants(product) {
      return this.matchingVariants(product).filter(function (variant) { return variant.available !== false; });
    }

    matchingProducts() {
      return this.products.filter(function (product) { return this.matchingVariants(product).length > 0; }, this);
    }

    productByHandle(handle) {
      return this.products.find(function (product) { return product.handle === handle; }) || null;
    }

    variantById(product, id) {
      if (!product) return null;
      return (product.variants || []).find(function (variant) { return String(variant.id) === String(id); }) || null;
    }

    variantAnywhere(id) {
      for (var i = 0; i < this.products.length; i += 1) {
        var variant = this.variantById(this.products[i], id);
        if (variant) return { product: this.products[i], variant: variant };
      }
      return null;
    }

    formatVariantOption(product, variant) {
      if (!variant) return '';
      var parts = (variant.options || []).map(clean).filter(function (option) {
        if (!option || option.toLowerCase() === 'default title') return false;
        if (this.productRequiresSizeMatch(product) && extractSize(option)) return false;
        return true;
      }, this);
      if (parts.length) return parts.join(' / ');
      var title = clean(variant.title);
      return title.toLowerCase() === 'default title' ? product.title : title;
    }

    variantChoiceKey(product, variant) {
      if (!variant) return '';
      return (variant.options || []).map(clean).filter(function (option) {
        if (!option || option.toLowerCase() === 'default title') return false;
        if (this.productRequiresSizeMatch(product) && extractSize(option)) return false;
        return true;
      }, this).join('');
    }

    selectionFor(product, variant, qty) {
      return {
        productHandle: product.handle,
        variantId: String(variant.id),
        choiceKey: this.variantChoiceKey(product, variant),
        label: this.formatVariantOption(product, variant),
        qty: Math.max(1, parseInt(qty, 10) || 1)
      };
    }

    resolve(selection, availableOnly) {
      if (!selection) return null;
      var product = this.productByHandle(selection.productHandle);
      var variant = this.variantById(product, selection.variantId);
      if (!product || !variant || !this.variantMatchesSize(product, variant)) return null;
      if (availableOnly && variant.available === false) return null;
      return { product: product, variant: variant };
    }

    // When the furniture size changes, carry each colour over to the matching size.
    migrate(list) {
      var kept = [];
      var dropped = [];
      list.forEach(function (selection) {
        var product = this.productByHandle(selection.productHandle);
        if (!product) { dropped.push(selection.label); return; }
        var available = this.availableVariants(product);
        var exact = available.find(function (variant) { return String(variant.id) === String(selection.variantId); });
        var semantic = available.find(function (variant) { return this.variantChoiceKey(product, variant) === selection.choiceKey; }, this);
        var target = exact || semantic;
        if (target) kept.push(this.selectionFor(product, target, selection.qty));
        else dropped.push(selection.label);
      }, this);
      return { kept: kept, dropped: dropped };
    }

    refresh() {
      var currentId = this.currentMainVariantId();
      var changed = !!(this.lastMainVariantId && currentId !== this.lastMainVariantId);
      if (changed) {
        var size = this.currentMainSize();
        var result = this.migrate(this.committed);
        this.committed = result.kept;
        if (result.dropped.length) {
          this.noticeMessage = result.dropped.join(', ') + (result.dropped.length === 1 ? ' is' : ' are') + ' unavailable' + (size ? ' for ' + size + 'cm' : '') + '. Choose another colour.';
        }
        if (this.isOpen) this.draft = this.migrate(this.draft).kept;
      }
      this.lastMainVariantId = currentId;
      this.render();
    }

    // ---------- studio state ----------
    open() {
      var products = this.matchingProducts();
      if (!products.length) return;
      this.returnFocus = document.activeElement;
      this.isOpen = true;
      this.draft = this.committed.map(cloneSelection);
      this.draftTouched = this.committedTouched;
      var firstProduct = products[0];
      var firstAvailable = this.availableVariants(firstProduct)[0];
      this.focusVariantId = this.draft.length ? this.draft[this.draft.length - 1].variantId : (firstAvailable ? String(firstAvailable.id) : '');
      if (!this.selectedProductHandle || !products.some(function (p) { return p.handle === this.selectedProductHandle; }, this)) {
        this.selectedProductHandle = (this.draft.length && this.draft[0].productHandle) || products[0].handle;
      }
      this.shell.hidden = false;
      if (this.styleName === 'inline-studio' && this.offer) this.offer.setAttribute('aria-expanded', 'true');
      this.setPageLocked(this.styleName !== 'inline-studio');
      this.render();
      window.requestAnimationFrame(function () {
        if (this.styleName === 'inline-studio') this.shell.scrollIntoView({ behavior: 'smooth', block: 'start' });
        else if (this.studio) this.studio.focus({ preventScroll: true });
      }.bind(this));
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.draft = [];
      this.shell.hidden = true;
      if (this.styleName === 'inline-studio' && this.offer) this.offer.setAttribute('aria-expanded', 'false');
      this.setPageLocked(false);
      this.render();
      if (this.returnFocus && typeof this.returnFocus.focus === 'function') this.returnFocus.focus();
    }

    draftEntry(variantId) {
      return this.draft.find(function (entry) { return String(entry.variantId) === String(variantId); }) || null;
    }

    // Looking is free: a tile click only changes the photo. Adding is its own button.
    viewTile(productHandle, variantId) {
      var product = this.productByHandle(productHandle);
      var variant = this.variantById(product, variantId);
      if (!product || !variant) return;
      this.focusVariantId = String(variantId);
      this.noticeMessage = '';
      this.render();
      this.announce('Viewing ' + this.formatVariantOption(product, variant) + (variant.available === false ? ', sold out.' : '.'));
    }

    addVariant(productHandle, variantId) {
      var product = this.productByHandle(productHandle);
      var variant = this.variantById(product, variantId);
      if (!product || !variant || variant.available === false || this.draftEntry(variantId)) return;
      var qty = (!this.draft.length && !this.draftTouched) ? this.mainQuantity() : 1;
      this.draft.push(this.selectionFor(product, variant, qty));
      this.focusVariantId = String(variantId);
      this.noticeMessage = '';
      this.render();
      this.announce(this.formatVariantOption(product, variant) + ' added, ' + qty + (qty === 1 ? ' cushion.' : ' cushions.'));
    }

    changeQty(variantId, delta) {
      var entry = this.draftEntry(variantId);
      if (!entry) return;
      entry.qty = Math.max(1, entry.qty + delta);
      this.draftTouched = true;
      this.focusVariantId = String(variantId);
      this.render();
      this.announce(entry.label + ': ' + entry.qty);
    }

    removeFromDraft(variantId) {
      var entry = this.draftEntry(variantId);
      this.draft = this.draft.filter(function (item) { return String(item.variantId) !== String(variantId); });
      this.draftTouched = true;
      if (this.focusVariantId === String(variantId)) this.focusVariantId = this.draft.length ? this.draft[this.draft.length - 1].variantId : '';
      this.render();
      if (entry) this.announce(entry.label + ' removed.');
    }

    confirm() {
      var valid = this.draft.filter(function (entry) { return !!this.resolve(entry, true); }, this);
      var hadSelection = this.committed.length > 0;
      this.committed = valid.map(cloneSelection);
      this.committedTouched = this.draftTouched;
      this.noticeMessage = '';
      var total = this.totalQty(this.committed);
      this.close();
      this.render();
      if (total) this.announce(total + (total === 1 ? ' cushion' : ' cushions') + ' selected. Added with your product when you add to cart.');
      else this.announce(hadSelection ? 'Cushions removed from your order.' : 'No cushion selected.');
    }

    removeAll() {
      this.committed = [];
      this.committedTouched = false;
      this.draft = [];
      this.noticeMessage = '';
      if (this.isOpen) this.close();
      this.render();
      this.announce('Cushions removed from your order.');
    }

    totalQty(list) {
      return list.reduce(function (sum, entry) { return sum + entry.qty; }, 0);
    }

    totalCents(list) {
      return list.reduce(function (sum, entry) {
        var found = this.resolve(entry, false);
        return sum + (found ? (parseInt(found.variant.price, 10) || 0) * entry.qty : 0);
      }.bind(this), 0);
    }

    setPageLocked(locked) {
      document.documentElement.classList.toggle('teak-cushion-configurator-open', !!locked);
    }

    handleDocumentKeydown(event) {
      if (!this.isOpen) return;
      if (event.key === 'Escape') { event.preventDefault(); this.close(); return; }
      if (event.key !== 'Tab' || this.styleName === 'inline-studio' || !this.studio) return;
      var focusable = Array.from(this.studio.querySelectorAll('a[href], button:not([disabled]), select:not([disabled]), input:not([disabled]), [tabindex="0"]')).filter(function (element) {
        return !element.hidden && element.offsetParent !== null;
      });
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }

    // ---------- rendering ----------
    render() {
      var products = this.matchingProducts();
      var hasFit = products.length > 0;
      var hasStock = products.some(function (product) { return this.availableVariants(product).length > 0; }, this);
      var active = this.committed.filter(function (entry) { return !!this.resolve(entry, true); }, this);

      this.classList.toggle('is-selected', active.length > 0);
      this.classList.toggle('is-unavailable', !hasFit);
      this.classList.toggle('is-sold-out', hasFit && !hasStock);
      if (this.offer) this.offer.disabled = !hasFit;

      this.renderOffer(active, products, hasStock);
      if (this.offerRemove) this.offerRemove.hidden = !active.length;
      this.updateCartLabels(active);

      if (!this.isOpen) return;

      var selectedProduct = this.productByHandle(this.selectedProductHandle);
      if (!selectedProduct || products.indexOf(selectedProduct) === -1) selectedProduct = products[0] || null;
      if (!selectedProduct) { this.renderNoMatch(); return; }
      this.selectedProductHandle = selectedProduct.handle;

      this.renderProductSelect(products, selectedProduct);
      this.renderTiles(selectedProduct);

      if (this.fitLabel) {
        var size = this.productRequiresSizeMatch(selectedProduct) ? this.currentMainSize() : '';
        this.fitLabel.textContent = size ? this.mainProduct.title + ' ' + size + 'cm' : this.mainLineLabel();
      }
      if (this.notice) {
        var soldOut = !this.availableVariants(selectedProduct).length ? 'Every matching colour is currently sold out.' : '';
        this.notice.textContent = this.noticeMessage || soldOut;
        this.notice.hidden = !this.notice.textContent;
      }
      this.renderSummary();
      this.renderVisual();
    }

    swatchMarkup(variant, name) {
      if (variant && variant.swatch) return '<img src="' + escapeHtml(variant.swatch) + '" alt="" loading="lazy">';
      return '<span class="teak-cushion-configurator__flat" style="background:' + escapeHtml(swatchBackground(name || (variant && variant.title) || '')) + '"></span>';
    }

    renderOffer(active, products, hasStock) {
      if (!products.length) {
        if (this.offerIcon) this.offerIcon.innerHTML = '';
        if (this.offerTitle) this.offerTitle.textContent = 'Matching cushion unavailable';
        if (this.offerMeta) this.offerMeta.textContent = 'No cushion matches the selected size';
        if (this.offerPrice) this.offerPrice.textContent = '';
        if (this.offerAction) this.offerAction.textContent = 'Unavailable';
        return;
      }
      var product = products[0];
      var available = this.availableVariants(product);
      var size = this.productRequiresSizeMatch(product) ? this.currentMainSize() : '';

      if (!active.length) {
        var fan = available.slice(0, 3);
        if (this.offerIcon) {
          this.offerIcon.innerHTML = '<span class="teak-cushion-configurator__fan">' + fan.map(function (variant) {
            return '<i>' + this.swatchMarkup(variant, this.formatVariantOption(product, variant)) + '</i>';
          }, this).join('') + '</span>';
        }
        if (this.offerTitle) this.offerTitle.textContent = 'Add a matching cushion';
        if (this.offerMeta) {
          this.offerMeta.textContent = hasStock
            ? available.length + (available.length === 1 ? ' colour' : ' colours') + (size ? ' · fits the ' + size + 'cm' : ' · cut to fit')
            : 'See colours and availability';
        }
        if (this.offerPrice) this.offerPrice.textContent = this.priceRange(available);
        if (this.offerAction) this.offerAction.textContent = hasStock ? 'Choose' : 'View';
        return;
      }

      var total = this.totalQty(active);
      if (this.offerIcon) {
        this.offerIcon.innerHTML = '<span class="teak-cushion-configurator__chosen">' + active.slice(0, 2).map(function (entry) {
          var found = this.resolve(entry, true);
          return '<i>' + this.swatchMarkup(found.variant, entry.label) + '</i>';
        }, this).join('') + '<span class="teak-cushion-configurator__count">×' + total + '</span></span>';
      }
      if (this.offerTitle) {
        this.offerTitle.textContent = active.length === 1
          ? active[0].label + ' cushion × ' + total
          : total + ' cushions in ' + active.length + ' colours';
      }
      if (this.offerMeta) {
        var mainQty = this.mainQuantity();
        var mirrors = active.length === 1 && total === mainQty && mainQty > 1;
        this.offerMeta.textContent = active.length === 1
          ? (mirrors ? 'One for each · ' : '') + (size ? 'fits the ' + size + 'cm' : 'added with this product')
          : active.map(function (entry) { return entry.label + ' × ' + entry.qty; }).join(', ');
      }
      if (this.offerPrice) this.offerPrice.innerHTML = '<span class="teak-cushion-configurator__tick" aria-hidden="true">✓</span>+ ' + this.formatMoney(this.totalCents(active));
      if (this.offerAction) this.offerAction.textContent = 'Edit';
    }

    renderNoMatch() {
      if (this.tilesHost) this.tilesHost.innerHTML = '';
      if (this.fitLabel) this.fitLabel.textContent = 'No matching size';
      if (this.notice) { this.notice.hidden = false; this.notice.textContent = 'No matching cushion is available for the selected size.'; }
      if (this.confirmButton) this.confirmButton.disabled = true;
      this.renderVisual();
    }

    renderProductSelect(products, selectedProduct) {
      if (!this.productField || !this.productSelect) return;
      this.productField.hidden = products.length <= 1;
      this.productSelect.innerHTML = '';
      products.forEach(function (product) {
        var option = document.createElement('option');
        option.value = product.handle;
        option.textContent = product.title;
        option.selected = product.handle === selectedProduct.handle;
        this.productSelect.appendChild(option);
      }, this);
    }

    renderTiles(product) {
      if (!this.tilesHost) return;
      var focused = document.activeElement && this.tilesHost.contains(document.activeElement) ? document.activeElement.closest('[data-cushion-tile]') : null;
      var focusedId = focused ? focused.dataset.variantId : '';
      var html = this.matchingVariants(product).map(function (variant) {
        var name = this.formatVariantOption(product, variant);
        var entry = this.draftEntry(variant.id);
        var out = variant.available === false;
        var viewing = this.focusVariantId === String(variant.id);
        var classes = 'teak-cushion-configurator__tile' + (entry ? ' is-on' : '') + (out ? ' is-out' : '') + (viewing ? ' is-viewing' : '');
        return '<div class="' + classes + '" data-cushion-tile data-variant-id="' + escapeHtml(variant.id) + '" data-product-handle="' + escapeHtml(product.handle) + '" role="button" tabindex="0" aria-pressed="' + (viewing ? 'true' : 'false') + '" aria-label="' + escapeHtml('View ' + name + ', ' + variant.priceFormatted + (out ? ', sold out' : entry ? ', ' + entry.qty + ' in your selection' : '')) + '">'
          + (entry ? '<span class="teak-cushion-configurator__tile-badge">×' + entry.qty + '</span>' : (viewing ? '<span class="teak-cushion-configurator__tile-badge teak-cushion-configurator__tile-badge--viewing">Viewing</span>' : ''))
          + '<span class="teak-cushion-configurator__tile-swatch">' + this.swatchMarkup(variant, name) + '</span>'
          + '<span class="teak-cushion-configurator__tile-name">' + escapeHtml(name) + (out ? ' <em>· Sold out</em>' : '') + '</span>'
          + '<span class="teak-cushion-configurator__tile-price">' + escapeHtml(variant.priceFormatted) + '</span>'
          + this.controlMarkup(product, variant, entry, out, viewing, false)
          + '</div>';
      }, this).join('');
      this.tilesHost.innerHTML = html;
      // On the mobile strip, keep the viewed colour in sight.
      if (this.isMobile() && this.focusVariantId) {
        var viewed = this.tilesHost.querySelector('[data-cushion-tile][data-variant-id="' + this.focusVariantId + '"]');
        if (viewed && typeof viewed.scrollIntoView === 'function') viewed.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
      }
      if (focusedId) {
        var again = this.tilesHost.querySelector('[data-cushion-tile][data-variant-id="' + focusedId + '"]');
        if (again) again.focus({ preventScroll: true });
      }
    }

    // Controls for one colour: Add when not chosen, a stepper with Remove once it is.
    controlMarkup(product, variant, entry, out, viewing, big) {
      var name = this.formatVariantOption(product, variant);
      var wrap = big ? 'teak-cushion-configurator__scene-controls' : 'teak-cushion-configurator__tile-qty';
      if (entry) {
        return '<span class="' + wrap + '"><span class="teak-cushion-configurator__stepper"><button type="button" data-cushion-qty="-1" data-variant-id="' + escapeHtml(variant.id) + '" aria-label="Fewer ' + escapeHtml(name) + '">−</button><output>' + entry.qty + '</output><button type="button" data-cushion-qty="1" data-variant-id="' + escapeHtml(variant.id) + '" aria-label="More ' + escapeHtml(name) + '">+</button></span>'
          + (big ? '<span class="teak-cushion-configurator__scene-added">Added</span>' : '')
          + '<button type="button" class="teak-cushion-configurator__tile-remove" data-cushion-remove-variant data-variant-id="' + escapeHtml(variant.id) + '">Remove</button></span>';
      }
      if (out) return big ? '<span class="' + wrap + '"><span class="teak-cushion-configurator__scene-soldout">Sold out</span></span>' : '';
      if (!viewing && !big) return '';
      var label = big ? 'Add ' + escapeHtml(name) + ' · ' + escapeHtml(variant.priceFormatted) : 'Add';
      return '<span class="' + wrap + '"><button type="button" class="teak-cushion-configurator__add" data-cushion-add data-product-handle="' + escapeHtml(product.handle) + '" data-variant-id="' + escapeHtml(variant.id) + '">' + label + '</button></span>';
    }

    renderSceneAction(found) {
      if (!this.sceneAction) return;
      if (!found) { this.sceneAction.hidden = true; this.sceneAction.innerHTML = ''; return; }
      var entry = this.draftEntry(found.variant.id);
      this.sceneAction.innerHTML = this.controlMarkup(found.product, found.variant, entry, found.variant.available === false, true, true);
      this.sceneAction.hidden = false;
    }

    renderSummary() {
      var count = this.totalQty(this.draft);
      if (this.summary) {
        this.summary.innerHTML = this.draft.length
          ? '<span class="teak-cushion-configurator__summary-count">' + count + (count === 1 ? ' cushion' : ' cushions') + '</span> ' + this.draft.map(function (entry) { return '<b>' + escapeHtml(entry.label) + '</b> × ' + entry.qty; }).join(' · ') + ' <span class="teak-cushion-configurator__summary-total">+ ' + this.formatMoney(this.totalCents(this.draft)) + '</span>'
          : 'Nothing added yet. Tap a colour to see it, then Add.';
      }
      if (this.confirmButton) {
        this.confirmButton.disabled = false;
        if (this.draft.length) this.confirmButton.textContent = 'Done';
        else this.confirmButton.textContent = this.committed.length ? (this.committed.length > 1 ? 'Remove cushions' : 'Remove cushion') : 'Close';
        this.confirmButton.classList.toggle('is-remove', !this.draft.length && this.committed.length > 0);
        var cancel = this.shell ? this.shell.querySelector('.teak-cushion-configurator__cancel') : null;
        if (cancel) cancel.hidden = !this.draft.length && !this.committed.length;
      }
    }

    renderVisual() {
      var found = this.focusVariantId ? this.variantAnywhere(this.focusVariantId) : null;
      var mainTitle = this.mainProduct.title || 'the selected product';
      this.renderSceneAction(found);
      if (!found || !this.sceneImage) {
        if (this.sceneImage) this.sceneImage.hidden = true;
        if (this.caption) this.caption.textContent = 'Tap a colour to see it on the ' + mainTitle + '.';
        return;
      }
      var name = this.formatVariantOption(found.product, found.variant);
      if (found.variant.scene) {
        if (this.sceneImage.getAttribute('src') !== found.variant.scene) this.sceneImage.src = found.variant.scene;
        this.sceneImage.alt = found.variant.sceneAlt || (mainTitle + ' with ' + name + ' cushion');
        this.sceneImage.hidden = false;
        if (this.caption) this.caption.innerHTML = '<b>' + escapeHtml(this.mainLineLabel()) + '</b> with ' + escapeHtml(name) + ' cushion';
      } else {
        this.sceneImage.hidden = true;
        if (this.caption) this.caption.innerHTML = escapeHtml(name) + ' cushion. No photo on the ' + escapeHtml(mainTitle) + ' yet; the fabric is shown on the tile.';
      }
    }

    priceRange(variants) {
      if (!variants || !variants.length) return '';
      var sorted = variants.slice().sort(function (a, b) { return a.price - b.price; });
      var lowest = sorted[0];
      var highest = sorted[sorted.length - 1];
      return lowest.price === highest.price ? '+ ' + lowest.priceFormatted : 'From ' + lowest.priceFormatted;
    }

    updateCartLabels(active) {
      if (!this.productRoot) return;
      var label = !active.length ? '' : (active.length === 1 ? 'Add both to cart' : 'Add all to cart');
      this.productRoot.querySelectorAll('[data-teak-pdetails-form] button[name="add"]').forEach(function (button) {
        var content = button.querySelector('.add-to-cart-text__content');
        var text = content ? content.querySelector('span span') : null;
        if (!text) return;
        if (!text.dataset.cushionDefaultLabel) text.dataset.cushionDefaultLabel = text.textContent.trim() || 'Add to cart';
        text.textContent = label || text.dataset.cushionDefaultLabel;
      });
      var stickyCta = this.productRoot.querySelector('[data-teak-pshow-stickycta]');
      var stickyLabel = this.productRoot.querySelector('[data-teak-pshow-stickycta-label]');
      if (stickyCta) stickyCta.dataset.addLabel = label || 'Add to cart';
      if (stickyCta && stickyLabel && stickyCta.dataset.state === 'add') stickyLabel.textContent = stickyCta.dataset.addLabel;
    }

    announce(message) {
      if (!this.status) return;
      this.status.textContent = '';
      window.requestAnimationFrame(function () { this.status.textContent = message || ''; }.bind(this));
    }

    // ---------- cart ----------
    // One cart line per chosen colour, each with its own quantity and a visible
    // "For" property so the cart, the order and the warehouse know which piece it belongs to.
    getItems() {
      var lineFor = this.mainLineLabel();
      return this.committed.map(function (entry) {
        var found = this.resolve(entry, true);
        if (!found) return null;
        return { id: found.variant.id, quantity: entry.qty, properties: { 'For': lineFor } };
      }, this).filter(Boolean);
    }

    // Legacy single-item API kept for older callers; the showcase uses getItems().
    getItem() {
      var items = this.getItems();
      return items.length === 1 ? { id: items[0].id, quantity: items[0].quantity } : null;
    }
  }

  customElements.define('teak-cushion-configurator', TeakCushionConfigurator);
}());
